/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { ChartStyle } from './style';
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';

const checkNumber = (num) => (typeof num === 'number' && !Number.isNaN(num));

const getCoordinatesForPercent = (percent) => [
  checkNumber(percent) ? Math.cos(2 * Math.PI * percent) : 0,
  checkNumber(percent) ? Math.sin(2 * Math.PI * percent) : 0,
];

const Chart = ({
  dataA: combinedCompletionRate = null,
  dataB: subjectSelfAssessment = null,
  active = null,
  setActive = () => { },
  children = null,
  renderTooltip = () => null,
  doConstrainsts = true,
  xDiff = 30,
  yDiff = 50,
  mainColor = '#2E90FA',
  secondColor = '#ABD6FF',
  svgBackground,
  doOpacity = true,
  svgTooltip = true,
}) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [mouseY, setMouseY] = useState(0);
  const [mouseX, setMouseX] = useState(0);
  const hasSelfAssessment = useMemo(() =>
    typeof subjectSelfAssessment === 'number', [subjectSelfAssessment]);

  const onMouseLeaveHandler = useCallback((e, type = '') => {
    let x = e.clientX - svgRef.current.getBoundingClientRect().left + xDiff;
    let y = e.clientY - svgRef.current.getBoundingClientRect().top + yDiff;

    if (doConstrainsts && containerRef.current.clientWidth - x < 230) {
      x = containerRef.current.clientWidth - 230;
    }

    if (doConstrainsts && containerRef.current.clientHeight - y < 130) {
      y = 0;
      x = containerRef.current.clientWidth / 2 - 100;
    }

    setMouseY(y);
    setMouseX(x);
    setActive(type);
  }, [svgRef, containerRef]);

  const combinedGraph = useMemo(() => {
    if (typeof combinedCompletionRate === 'number') {
      let cumulativePercent = 0;
      const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
      cumulativePercent += checkNumber(combinedCompletionRate) ? combinedCompletionRate : 0;
      const [endX, endY] = getCoordinatesForPercent(cumulativePercent);
      const largeArcFlag = combinedCompletionRate > .5 ? 1 : 0;

      const pathData = [
        `M ${startX} ${startY}`,
        `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        `L 0 0`,
      ].join(' ');

      return (
        <path
          d={pathData}
          fill={mainColor}
          {...(!svgTooltip ? {
            onMouseEnter: () => {
              if (hasSelfAssessment) {
                setActive('COMBINED');
              }
            },
            onMouseLeave: () => {
              if (hasSelfAssessment) {
                setActive(null);
              }
            },
            onMouseMove: (e) => onMouseLeaveHandler(e, 'COMBINED'),
          } : {})}
          strokeLinecap="round" strokeLinejoin="round"
        />
      );
    }

    return null;
  }, [combinedCompletionRate, hasSelfAssessment]);

  const selfAssessment = useMemo(() => {
    if (typeof subjectSelfAssessment === 'number') {
      if (svgBackground) {
        let cumulativePercent = 0;

        return [
          {
            percent: subjectSelfAssessment,
            color: 'transparent',
          },
          {
            percent: 1 - subjectSelfAssessment,
            color: '#EDEDED',
          },
        ].map((d, i) => {
          const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
          cumulativePercent += d.percent - 0;
          const [endX, endY] = getCoordinatesForPercent(cumulativePercent);
          const [borderStartX, borderStartY] = getCoordinatesForPercent(cumulativePercent);
          cumulativePercent += 0;
          const [borderEndX, borderEndY] = getCoordinatesForPercent(cumulativePercent);
          const largeArcFlag = d.percent > .5 ? 1 : 0;

          const pathData = [
            `M ${startX} ${startY}`,
            `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
            `L 0 0`,
          ].join(' ');

          const borderPathData = [
            `M ${borderStartX} ${borderStartY}`,
            `A 1 1 0 0 1 ${borderEndX} ${borderEndY}`,
            `L 0 0`,
          ].join(' ');

          return (
            <Fragment key={i.toString()}>
              <path
                d={pathData}
                fill={d.color}
                {...(!svgTooltip ? {
                  onMouseEnter: () => setActive('SELF'),
                  onMouseLeave: () => setActive(null),
                  onMouseMove: (e) => onMouseLeaveHandler(e, 'SELF'),
                } : {})}
              />
              <path
                d={borderPathData}
                fill="white"
              />
            </Fragment>
          );
        });
      }
      let cumulativePercent = 0;
      const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
      cumulativePercent += checkNumber(subjectSelfAssessment) ? subjectSelfAssessment : 0;
      const [endX, endY] = getCoordinatesForPercent(cumulativePercent);
      const largeArcFlag = subjectSelfAssessment > .5 ? 1 : 0;

      const pathData = [
        `M ${startX} ${startY}`,
        `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        `L 0 0`,
      ].join(' ');

      return (
        <path
          d={pathData}
          fill={secondColor}
          onMouseEnter={() => setActive('SELF')}
          onMouseLeave={() => setActive(null)}
          onMouseMove={(e) => onMouseLeaveHandler(e, 'SELF')}
          style={{ zIndex: 2 }}
        />
      );
    }

    return null;
  }, [subjectSelfAssessment]);

  const tooltip = active ? renderTooltip({ mouseX, mouseY }) : null;

  const _onSvgMouseEnter = () => {
    if (subjectSelfAssessment) {
      setActive('SELF');
    }
    if (svgTooltip) {
      setActive('SELF');
    }
  }

  const _onSvgMouseLeave = () => {
    if (subjectSelfAssessment) {
      setActive(null);
    }
    if (svgTooltip) {
      setActive(null);
    }
  }

  const _onSvgMouseMove = (e) => {
    if (subjectSelfAssessment) {
      onMouseLeaveHandler(e, 'SELF');
    }
    if (svgTooltip) {
      onMouseLeaveHandler(e, 'SELF');
    }
  }

  return (
    <ChartStyle ref={containerRef}>
      <svg
        viewBox="-1 -1 2 2"
        className="main-svg"
        style={{ opacity: doOpacity ? active === 'SELF' ? 0.4 : 1 : 1 }}
        ref={svgRef}
        {...(svgTooltip ? {
          onMouseEnter: () => {
            if (hasSelfAssessment) {
              setActive('COMBINED');
            }
          },
          onMouseLeave: () => {
            if (hasSelfAssessment) {
              setActive(null);
            }
          },
          onMouseMove: (e) => onMouseLeaveHandler(e, 'COMBINED'),
        } : {})}
      >
        {combinedGraph || null}
      </svg>
      <div className={formatClassname(['first-cont', children && 'custom'])}>
        {children}
        {selfAssessment && (
          <>
            {!!svgBackground && (
              <svg
                className="placeholder-cont"
                onMouseEnter={_onSvgMouseEnter}
                onMouseLeave={_onSvgMouseLeave}
                onMouseMove={_onSvgMouseMove}
              >
                <circle cx="50%" cy="50%" r="50%" stroke="white" strokeWidth="6%" fill="transparent" />
              </svg>
            )}
            <svg viewBox="-1 -1 2 2"
              style={{
                opacity: doOpacity ? active === 'COMBINED' ? 0.4 : 1 : 1,
                backgroundImage: svgBackground,
              }}
              {...(svgTooltip ? {
                onMouseEnter: () => setActive('SELF'),
                onMouseLeave: () => setActive(null),
                onMouseMove: (e) => onMouseLeaveHandler(e, 'SELF'),
              } : {})}
            >
              {selfAssessment}
            </svg>
          </>
        )}
      </div>
      {!children && (
        <div className="second-cont" />
      )}
      {tooltip}
    </ChartStyle>
  );
};

export default Chart;
